import { Link } from "wouter";
import { ArrowRight, Check, MapPin, Clock, Truck, Shield, Zap, Target } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead, { createLocalBusinessSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES, CONTACT } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";

const localBenefits = [
  {
    icon: Zap,
    title: "Lokal partner, noll transporttid",
    text: "Eftersom vi finns direkt i Eskilstuna har vi ingen transportsträcka. Hämta dina detaljer direkt från vår verkstad samma dag.",
  },
  {
    icon: Clock,
    title: "Korta ledtider för lokala företag",
    text: "Med tre CO2-lasrar från Trumpf och Amada har vi en enorm kapacitet och kan prioritera brådskande jobb från företag i Eskilstuna.",
  },
  {
    icon: Shield,
    title: "Garanterad precision i varje detalj",
    text: "Vi säkerställer toleranser ner till 0,1 mm. Som lokal kund kan du enkelt besöka oss för att diskutera och inspektera dina komponenter.",
  },
  {
    icon: Target,
    title: "Allt under ett tak",
    text: "Kombinera laserskärning med kantpressning, svetsning och montering. En komplett produktionspartner här i Eskilstuna.",
  },
];

const eskilstunaIndustries = [
  {
    name: "Tillverkningsindustri",
    desc: "Eskilstuna är ett nav för tillverkning. Vi är en stolt leverantör till lokala industriföretag, från små verkstäder till stora koncerner.",
  },
  {
    name: "Logistik & Transport",
    desc: "Med Eskilstunas logistikpark som granne levererar vi komponenter till transport- och logistiksektorn, inklusive påbyggnader och specialfordon.",
  },
  {
    name: "Bygg & Entreprenad",
    desc: "Vi tillverkar skräddarsydda byggnadselement, från balkar och konsoler till designade fasadplåtar, för lokala byggprojekt.",
  },
  {
    name: "Tech & Innovation",
    desc: "Prototyper och serietillverkning för Eskilstunas växande tech-scen. Snabb produktion för att accelerera innovationscykeln.",
  },
];

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
];

export default function LaserskarningEskilstuna() {
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Laserskärning Eskilstuna",
    description:
      "Lokal, professionell laserskärning för företag i Eskilstuna. ASPAB erbjuder CO2 Laser-skärning med toleranser ner till 0,1 mm, omedelbar tillgänglighet och brett materialutbud.",
    provider: {
      "@type": "LocalBusiness",
      name: "ASPAB",
      telephone: "+46161326 00",
      email: "order@aspab.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Abramsons väg 2",
        addressLocality: "Eskilstuna",
        postalCode: "632 39",
        addressCountry: "SE",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Eskilstuna",
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    serviceType: "Laserskärning",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Laserskärningstjänster",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning stål",
            description: "Laserskärning i svart stål upp till 25 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning rostfritt",
            description: "Laserskärning i rostfritt stål upp till 20 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning aluminium",
            description: "Laserskärning i aluminium upp till 15 mm tjocklek",
          },
        },
      ],
    },
  };

  const localBusinessSchema = createLocalBusinessSchema({
    areaServed: "Eskilstuna",
    serviceDescription: "Lokal laserskärning, kantpressning och svetsning för företag i Eskilstuna. Omgående leverans och möjlighet till expressproduktion.",
  });

  return (
    <Layout>
      <SEOHead
        title="Laserskärning Eskilstuna – Lokal partner med 0,1 mm precision | ASPAB"
        description="Laserskärning direkt i Eskilstuna. ASPAB är din lokala partner för CO2-laserskärning med 0,1 mm tolerans. Stål, rostfritt & aluminium. Begär offert idag!"
        canonical="https://aspab.com/laserskarning-eskilstuna"
        path="/laserskarning-eskilstuna"
        keywords="laserskärning Eskilstuna, plåtbearbetning Eskilstuna, CO2 Laser Eskilstuna, metallbearbetning Eskilstuna, lokal laserskärning"
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Eskilstuna", href: "/laserskarning-eskilstuna" },
        ]}
        structuredData={[localServiceSchema, localBusinessSchema]}
        ogImage={IMAGES.ogHem} // Assuming you have a specific OG image
      />

      <PageHero
        title="Laserskärning Eskilstuna"
        subtitle="Din lokala expert på laserskärning. Precision, kvalitet och oslagbar snabbhet direkt från vår anläggning i Eskilstuna."
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Eskilstuna", href: "/laserskarning-eskilstuna" },
        ]}
        image={IMAGES.heroLaser}
      />

      {/* Intro – Varför välja ASPAB i Eskilstuna */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Laserskärning på hemmaplan
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Din lokala partner för laserskärning i Eskilstuna
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  För företag i Eskilstuna är ASPAB det självklara valet. Vi är inte bara en leverantör; vi är din granne.
                  Med vår anläggning här i staden eliminerar vi transporttider och onödiga kostnader.
                  Du får omedelbar service, personlig kontakt och flexibiliteten som bara en lokal partner kan erbjuda.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi är djupt rotade i Eskilstunas industri och stolta över att stödja lokala företag.
                  Oavsett om du behöver en enstaka prototyp eller löpande serieproduktion,
                  är vår verkstad öppen för dig. Välkommen in för att diskutera ditt projekt!
                </p>
                <div className="flex items-center gap-3 mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" />
                  <div>
                    <span className="text-gray-900 font-medium text-sm">ASPAB: Mitt i Eskilstuna</span>
                    <span className="text-gray-500 text-sm block">Noll transporttid – hämta direkt hos oss</span>
                  </div>
                </div>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Begär offert – svar inom 24 timmar <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <LazyImage
                  src={IMAGES.laserOldSite} // Consider a local image
                  alt="Laserskärning i Eskilstuna – ASPAB:s lokala CO2-lasermaskiner"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">0,1 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Tolerans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">Samma dag</div>
                    <div className="text-gray-500 text-sm mt-1">Möjlig leverans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">3 st</div>
                    <div className="text-gray-500 text-sm mt-1">CO2-lasrar</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">30+</div>
                    <div className="text-gray-500 text-sm mt-1">Års erfarenhet</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Fördelar med ASPAB för Eskilstuna */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Lokala fördelar
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Varför Eskilstunaföretag väljer ASPAB
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Som en lokal aktör erbjuder vi en unik kombination av världsledande teknik och personlig service.
                Här är varför vi är det självklara valet för industrin i Eskilstuna.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {localBenefits.map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="bg-white p-6 rounded-lg border border-gray-200 h-full">
                  <item.icon className="w-8 h-8 text-[#2563EB] mb-4" />
                  <h3 className="font-heading text-lg font-bold text-gray-900">{item.title}</h3>
                  <p className="text-gray-500 mt-2 text-sm">{item.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Material & kapacitet */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Material & Kapacitet
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Från tunnplåt till grovplåt i stål, rostfritt och aluminium
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Våra tre CO2-lasermaskiner från Trumpf och Amada är optimerade för att skära en mängd olika material och tjocklekar med högsta precision.
                  Vi lagerhåller de vanligaste materialen för att kunna erbjuda snabbast möjliga service till dig i Eskilstuna.
                </p>
                <div className="mt-8 space-y-4">
                  {capabilities.map((cap, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="bg-white p-2 rounded-full border border-gray-200">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-grow">
                        <div className="font-semibold text-gray-800">{cap.material}</div>
                        <div className="text-sm text-gray-500">
                          Tjocklek: {cap.minThickness} – {cap.maxThickness}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.detail}
                alt="Material för laserskärning i Eskilstuna: Stål, Rostfritt, Aluminium"
                className="w-full rounded-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Industrier vi servar i Eskilstuna */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14 text-center mx-auto">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Lokala industrier
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Stolta leverantörer till Eskilstunas industri
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Vi har äran att samarbeta med många av de ledande företagen inom Eskilstunas kärnindustrier.
                Här är några av de sektorer där våra laserskurna komponenter spelar en viktig roll.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            {eskilstunaIndustries.map((industry, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="bg-white p-8 rounded-lg border border-gray-200 h-full">
                  <h3 className="font-heading text-xl font-bold text-gray-900">{industry.name}</h3>
                  <p className="text-gray-500 mt-2">{industry.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 md:py-28">
        <div className="container text-center">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
                Redo att starta ditt projekt i Eskilstuna?
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Kontakta oss idag för en kostnadsfri offert. Ladda upp dina ritningar (STEP, DXF, DWG) så återkommer vi med pris och leveranstid – ofta inom 24 timmar.
              </p>
              <Link
                href="/kontakt"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-8 text-lg"
              >
                Begär offert nu <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
